import React, { useState, useEffect } from 'react';
import SendMessageModal from '../components/SendMessageModal';
import './Equity.css';

function Equity() {
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');

  useEffect(() => {
    // Track page view with GTM dataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'page_view',
      page_title: 'Equity & Fund Management - rkube',
      page_location: window.location.href,
      page_path: window.location.pathname
    });
  }, []);

  const handleOpenModal = (type) => {
    setModalType(type);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalType('');
  };

  return (
    <div className="service-page-container">
      <div className="services-page-hero enhanced">
        <div className="container">
          <h1>Equity and Fund management</h1>
          <p className="services-page-description">
            Issue shares, manage capitalization tables, and automate equity tracking. 
            From SAFEs to structured fund administration, we help you stay compliant and 
            raise with confidence. We are trained to use tools like Carta and Carta Prime 
            to manage your equity and fund administration.
          </p>
          
          <div className="services-intro">
            <p>From cap table management to fund administration, we provide comprehensive equity solutions that help you maintain accurate records, ensure compliance, and scale your equity operations efficiently.</p>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="service-page-content">
          <section className="services-features-grid">
            <div className="services-card">
              <div className="services-card-header">
                <div className="services-card-icon green">
                  <span className="icon">📊</span>
                </div>
                <h3>Capitalization Tables</h3>
              </div>
              <p>Meticulous management of equity capitalization tables with clear, precise reporting. Track ownership, dilution, and equity changes in real-time with our advanced cap table management system.</p>
            </div>

            <div className="services-card">
              <div className="services-card-header">
                <div className="services-card-icon blue">
                  <span className="icon">📈</span>
                </div>
                <h3>Financial Reporting</h3>
              </div>
              <p>Tailored financial reporting focused specifically on equity management. Generate comprehensive reports for investors, board members, and stakeholders with detailed equity analytics and insights.</p>
            </div>

            <div className="services-card">
              <div className="services-card-header">
                <div className="services-card-icon purple">
                  <span className="icon">💼</span>
                </div>
                <h3>Fund Administration</h3>
              </div>
              <p>Efficient, expert fund administration support with precise records and compliance tracking. From fund formation to ongoing administration, we ensure your fund operations run smoothly and compliantly.</p>
            </div>

            <div className="services-card">
              <div className="services-card-header">
                <div className="services-card-icon gold">
                  <span className="icon">📝</span>
                </div>
                <h3>Equity Documentation</h3>
              </div>
              <p>Streamlined preparation and management of equity documentation and agreements. Handle everything from SAFEs to stock option plans with our comprehensive documentation management system.</p>
            </div>
          </section>

          <section className="service-benefits">
            <h2>Benefits</h2>
            <ul className="benefits-list">
              <li>Maintain accurate and up-to-date cap tables</li>
              <li>Simplify equity issuance and tracking</li>
              <li>Ensure compliance with regulatory requirements</li>
              <li>Provide clear reporting to investors and stakeholders</li>
              <li>Scale your equity and fund management efficiently</li>
            </ul>
          </section>

          {/* Equity & Fund Management Pricing-style Cards */}
          <section className="management-pricing-section equity-management-cards">
            <div className="pricing-container">
              {/* Equity Management Card */}
              <div className="pricing-card equity-management">
                <div className="management-card-header">
                  <h3 className="management-title">All Equity Management</h3>
                  <p className="management-subtitle">From issuing early equity to IPO, build and scale your business on one platform</p>
                </div>
                <hr className="management-divider" />
                <ul className="management-features-list">
                  <li><span className="checkmark">✓</span><span><strong>Cap Table Management:</strong> Issue, track, and process payments for your company's equity securely</span></li>
                  <li><span className="checkmark">✓</span><span><strong>Equity Advisory:</strong> Empower your team with actionable tax and equity knowledge</span></li>
                  <li><span className="checkmark">✓</span><span><strong>409A Valuations:</strong> Enter audits with confidence with tailored, cost-effective valuations</span></li>
                  <li><span className="checkmark">✓</span><span><strong>Total Compensation:</strong> Get reliable salary and equity benchmarks</span></li>
                  <li><span className="checkmark">✓</span><span><strong>SAFE Financings:</strong> Issue SAFEs, collect signatures, and move money securely on one platform</span></li>
                  <li><span className="checkmark">✓</span><span><strong>Liquidity:</strong> Execute secondary transactions that fully integrate with your cap table</span></li>
                  <li><span className="checkmark">✓</span><span><strong>Financial Reporting:</strong> Generate your latest stock-based compensation reports in a few clicks</span></li>
                  <li><span className="checkmark">✓</span><span><strong>QSBS Attestation:</strong> Optimize your equity tax benefit with the QSBS tax exemption</span></li>
                </ul>
                <button 
                  className="get-started-button" 
                  onClick={() => handleOpenModal('equity')}
                >
                  Contact Us
                </button>
              </div>

              {/* Fund Management Card */}
              <div className="pricing-card fund-management">
                <div className="management-card-header">
                  <h3 className="management-title">All Fund Management</h3>
                  <p className="management-subtitle">Leverage a suite of products and services to support your firm at every stage</p>
                </div>
                <hr className="management-divider" />
                <ul className="management-features-list">
                  <li><span className="checkmark">✓</span><span><strong>Fund Administration:</strong> Automate your back office with solutions from scenario modeling to closings</span></li>
                  <li><span className="checkmark">✓</span><span><strong>Tactyc Forecasting & Planning:</strong> Guide your fund's strategy with data-driven precision</span></li>
                  <li><span className="checkmark">✓</span><span><strong>Fund Formations:</strong> Form your fund, administer the first close, and execute capital calls in six weeks</span></li>
                  <li><span className="checkmark">✓</span><span><strong>ASC 820:</strong> Get audit-ready valuations for consistent financial reporting</span></li>
                  <li><span className="checkmark">✓</span><span><strong>Fund Tax:</strong> Unlock a seamless tax season with your fund accounting and taxes in one place</span></li>
                  <li><span className="checkmark">✓</span><span><strong>Capital Call Line of Credit:</strong> Access capital outside your regular call cadence</span></li>
                  <li><span className="checkmark">✓</span><span><strong>SPVs:</strong> Form, close, and administer your SPV in just a few clicks</span></li>
                </ul>
                <button 
                  className="get-started-button" 
                  onClick={() => handleOpenModal('fund')}
                >
                  Contact Us
                </button>
              </div>
            </div>
          </section>

          {/* Tools Flexibility Section - moved below cards */}
          <section className="tools-flexibility">
            <h2>Flexible Equity Management Solutions</h2>
            <p>Our adaptive approach to equity management means you can choose the tools and services that best fit your needs.</p>
            
            <div className="tools-options">
              <div className="tool-option">
                <div className="tool-option-icon">
                  <span>🔄</span>
                </div>
                <h3>Use Our Platform</h3>
                <p>Access our comprehensive equity management platform that integrates with Carta and Carta Prime, providing seamless cap table management and fund administration.</p>
              </div>
              
              <div className="tool-option">
                <div className="tool-option-icon">
                  <span>⚙️</span>
                </div>
                <h3>Custom Integration</h3>
                <p>Already using Carta or other equity management tools? We can work with your existing systems to provide expert administration and support services.</p>
              </div>
            </div>
          </section>
        </div>
      </div>

      {showModal && (
        <SendMessageModal
          formName={modalType === 'fund' ? 'Fund Management Inquiry' : 'Equity Management Inquiry'}
          onClose={handleCloseModal}
          showDatePicker={false}
        />
      )}
    </div>
  );
}

export default Equity; 